import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Descriptions, Popconfirm, Space, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';

import {
  useCarrierUpdate,
  useModeCreate,
  useModeRemove,
  useModeUpdate,
} from '../../hooks/apiHooks';
import { UserRole } from '../../models/User';
import Container from '../common/Container';
import EditButton from '../common/EditButton';
import ListHeader from '../common/ListHeader';
import PermissionsToggle from '../common/PermissionsToggle';
import Toggle from '../common/Toggle';
import ModeModal from '../modals/ModeModal';

/*
interface IModeListProps {
  id: string;
  carrier: Carrier;
  modes: Mode[];
  userRole?: UserRole;
}
*/

const ModeList = ({ carrier, modes, userRole }) => {
  const createMutation = useModeCreate();
  const updateMutation = useModeUpdate();
  const removeMutation = useModeRemove();
  const carrierUpdateMutation = useCarrierUpdate();

  const [isEditing, setIsEditing] = useState(false);
  const [editableMode, setEditableMode] = useState();
  const [deletableMode, setDeletableMode] = useState();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const onAdd = useCallback(
    (mode) => {
      mode.carrierId = carrier.id;
      createMutation.mutate(mode);
    },
    [carrier.id, createMutation]
  );

  const onUpdate = useCallback(
    (mode) => {
      updateMutation.mutate(mode);
    },
    [updateMutation]
  );

  const onDelete = useCallback(() => {
    if (deletableMode) {
      removeMutation.mutate(deletableMode);
    }
  }, [deletableMode, removeMutation]);

  const onEdit = useCallback((mode) => {
    setEditableMode(mode);
    setShowEditModal(true);
  }, []);

  const onToggleActive = useCallback(
    (mode) => {
      mode.active = !mode.active;
      onUpdate(mode);
      setEditableMode(mode);
    },
    [onUpdate]
  );

  const onTogglePermission = useCallback(
    (permission) => {
      const newCarrier = {
        ...carrier,
        modePermission: permission,
      };
      carrierUpdateMutation.mutate(newCarrier);
    },
    [carrier, carrierUpdateMutation]
  );

  useEffect(() => {
    if (!isEditing) {
      setDeletableMode(undefined);
    }
  }, [isEditing]);

  useEffect(() => {
    if (createMutation.isSuccess) {
      setShowAddModal(false);
    }
  }, [createMutation.isSuccess]);

  useEffect(() => {
    if (updateMutation.isSuccess) {
      setShowEditModal(false);
    }
  }, [updateMutation.isSuccess]);

  return (
    <Container id="modes">
      <Descriptions
        colon={false}
        column={4}
        layout="vertical"
        title={
          <ListHeader>
            <Space direction="horizontal">
              <span>Modes of Transport</span>
              {userRole === UserRole.SUPER_ADMIN && (
                <PermissionsToggle
                  defaultValue={carrier.modePermission}
                  submitting={carrierUpdateMutation.isLoading}
                  onChange={onTogglePermission}
                />
              )}
            </Space>

            <Space>
              <EditButton
                color={isEditing ? '#389e0d' : undefined}
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'Done' : 'Edit'}
              </EditButton>
              <Button
                ghost
                style={{ width: 120 }}
                type="primary"
                onClick={() => setShowAddModal(true)}
              >
                Add Mode
              </Button>
            </Space>
          </ListHeader>
        }
      >
        {modes.map((mode, index) => (
          <Descriptions.Item key={index}>
            <Space direction="horizontal">
              <Toggle
                checked={mode.active}
                disabled={
                  !isEditing || (updateMutation.isLoading && !!editableMode)
                }
                loading={
                  updateMutation.isLoading && editableMode?.id === mode.id
                }
                size="small"
                onChange={() => onToggleActive(mode)}
              />
              <Typography.Text strong>{mode.name}</Typography.Text>
              {isEditing && !mode.defaultModeId && (
                <Space>
                  <Button
                    icon={<EditOutlined />}
                    shape="circle"
                    size="small"
                    onClick={() => onEdit(mode)}
                  />
                  <Popconfirm
                    okButtonProps={{ loading: removeMutation.isLoading }}
                    title="Are you sure to delete this mode?"
                    visible={deletableMode?.id === mode.id}
                    onCancel={() => setDeletableMode(undefined)}
                    onConfirm={onDelete}
                  >
                    <Button
                      danger
                      icon={<CloseOutlined />}
                      shape="circle"
                      size="small"
                      onClick={() => setDeletableMode(mode)}
                    />
                  </Popconfirm>
                </Space>
              )}
            </Space>
          </Descriptions.Item>
        ))}
        {modes.length % 4 === 3 && (
          <Descriptions.Item>&nbsp;</Descriptions.Item>
        )}
        {modes.length % 4 === 2 && (
          <Descriptions.Item>&nbsp;</Descriptions.Item>
        )}
        {modes.length % 4 === 1 && (
          <Descriptions.Item>&nbsp;</Descriptions.Item>
        )}
      </Descriptions>

      {showAddModal && (
        <ModeModal
          error={createMutation.error}
          submitting={createMutation.isLoading}
          onClose={() => setShowAddModal(false)}
          onSubmit={onAdd}
        />
      )}
      {showEditModal && editableMode && (
        <ModeModal
          error={updateMutation.error}
          selectedMode={editableMode}
          submitting={updateMutation.isLoading}
          onClose={() => setShowEditModal(false)}
          onSubmit={onUpdate}
        />
      )}
    </Container>
  );
};

export default ModeList;
